/**
 * Shared pivot table types for dynamic reporting API
 * Supports hierarchical grouping: Region → Store Group → Location → Employee Group → Job Role Family → Employee
 */
import { z } from "zod";
import { uuidSchema, dateStringSchema } from "./validation-common.js";
// ===== ENUMS & CONSTANTS =====
/**
 * Date aggregation granularity for pivot table buckets
 * - day: Daily buckets
 * - week: Weekly buckets (Sunday start)
 * - month: Monthly buckets
 * - quarter: Quarterly buckets (Q1-Q4)
 * - year: Yearly buckets
 */
export const DateAggregationSchema = z.enum([
    "day",
    "week",
    "month",
    "quarter",
    "year",
]);
/**
 * Aggregation methods for pivot table values
 * - SUM: Sum all values
 * - AVG: Average all values
 * - MAX: Maximum value
 * - MIN: Minimum value
 * - PERCENT_OF_TOTAL: Value as percentage of grand total
 */
export const PivotAggregationMethodSchema = z.enum([
    "SUM",
    "AVG",
    "MAX",
    "MIN",
    "PERCENT_OF_TOTAL",
]);
/**
 * Hierarchy levels that can appear in the pivot table tree
 * Order is fixed when present: REGION_TAG → STORE_GROUP_TAG → LOCATION → EMPLOYEE_GROUP_TAG → JOB_ROLE_FAMILY → EMPLOYEE
 */
export const HierarchyLevelSchema = z.enum([
    "REGION_TAG",
    "STORE_GROUP_TAG",
    "LOCATION",
    "EMPLOYEE_GROUP_TAG",
    "JOB_ROLE_FAMILY",
    "EMPLOYEE",
]);
/**
 * Node types in the pivot response tree
 */
export const PivotNodeTypeSchema = z.enum([
    "TAG",
    "LOCATION",
    "JOB_ROLE_FAMILY",
    "EMPLOYEE",
]);
// ===== REQUEST SCHEMAS =====
/**
 * Hierarchy configuration - defines which levels to include in the tree
 */
export const HierarchyConfigSchema = z.object({
    // Location-based groupings
    /** Include individual locations (default: true) */
    includeLocations: z.boolean().optional().default(true),
    /** Specific district Tag IDs for location grouping (region level) */
    districtIds: z.array(uuidSchema).optional(),
    /** Specific location group Tag IDs for location grouping (store group level) */
    locationGroupIds: z.array(uuidSchema).optional(),
    // Employee-based groupings (only valid for supportsEmployeeScope series)
    /** Specific user group Tag IDs for employee grouping */
    userGroupIds: z.array(uuidSchema).optional(),
    /** Group by job role family */
    includeJobRoleFamilies: z.boolean().optional().default(false),
    /** Include individual employees (leaf level) */
    includeEmployees: z.boolean().optional().default(false),
});
/**
 * Optional filters to narrow results before grouping
 */
export const PivotFiltersSchema = z.object({
    /** Filter to specific locations */
    locationIds: z.array(uuidSchema).optional(),
    /** Filter to specific employees */
    employmentIds: z.array(uuidSchema).optional(),
    /** Filter to specific job role families */
    jobRoleFamilyIds: z.array(uuidSchema).optional(),
});
// ===== DERIVED SERIES SCHEMAS =====
/**
 * Mathematical operations for derived series
 * - +: Addition (seriesA + seriesB)
 * - -: Subtraction (seriesA - seriesB)
 * - /: Division (seriesA / seriesB), returns 0 for division by zero
 * - ×: Multiplication (seriesA × seriesB)
 */
export const DerivedSeriesOperationSchema = z.enum(["+", "-", "/", "×"]);
/**
 * Definition for a derived series computed on-the-fly
 * Combines two existing series with a mathematical operation
 */
export const DerivedSeriesDefinitionSchema = z.object({
    /** User-provided display name for the derived series */
    name: z.string().min(1).max(100),
    /** First series type (must be in seriesTypes array) */
    seriesA: z.string().min(1),
    /** Mathematical operation to apply */
    operation: DerivedSeriesOperationSchema,
    /** Second series type (must be in seriesTypes array) */
    seriesB: z.string().min(1),
});
/**
 * Request schema for POST /api/reports/pivot-table
 */
export const PivotTableRequestSchema = z.object({
    // What to measure
    /** One or more series types to include (e.g., ["transaction_total", "labor_hours"]) */
    seriesTypes: z.array(z.string().min(1)).min(1),
    /** Aggregation method to apply */
    aggregationMethod: PivotAggregationMethodSchema,
    // Time range
    /** Start date (inclusive), ISO string */
    startDate: dateStringSchema,
    /** End date (exclusive), ISO string */
    endDate: dateStringSchema,
    /** Date bucketing granularity */
    dateAggregation: DateAggregationSchema,
    // Hierarchy levels to include
    hierarchy: HierarchyConfigSchema,
    // Optional filters
    filters: PivotFiltersSchema.optional(),
    // Derived series (computed on-the-fly)
    /**
     * Optional array of derived series to compute from base series
     * Max 10 per request, each must reference series in seriesTypes array
     */
    derivedSeries: z.array(DerivedSeriesDefinitionSchema).max(10).optional(),
});
// ===== HELPER FUNCTIONS =====
/**
 * Get the hierarchy levels that should be included based on config
 */
export function getActiveHierarchyLevels(config) {
    const levels = [];
    // Location-side hierarchy (fixed order)
    if (config.districtIds && config.districtIds.length > 0) {
        levels.push("REGION_TAG");
    }
    if (config.locationGroupIds && config.locationGroupIds.length > 0) {
        levels.push("STORE_GROUP_TAG");
    }
    if (config.includeLocations !== false) {
        levels.push("LOCATION");
    }
    // Employee-side hierarchy (fixed order)
    if (config.userGroupIds && config.userGroupIds.length > 0) {
        levels.push("EMPLOYEE_GROUP_TAG");
    }
    if (config.includeJobRoleFamilies) {
        levels.push("JOB_ROLE_FAMILY");
    }
    if (config.includeEmployees) {
        levels.push("EMPLOYEE");
    }
    return levels;
}
/**
 * Check if any employee-level grouping is requested
 */
export function hasEmployeeGrouping(config) {
    return !!(config.userGroupIds?.length ||
        config.includeJobRoleFamilies ||
        config.includeEmployees);
}
